/*
===============================================================================================================
                                 Copyright 2025-2026, Blue Yonder Group, Inc.
                                              All Rights Reserved

                                THIS IS UNPUBLISHED PROPRIETARY SOURCE CODE OF
                                            BLUE YONDER GROUP, INC.

                            The copyright notice above does not evidence any actual
                                 or intended publication of such source code.

===============================================================================================================
*/

/* eslint-disable no-param-reassign */
import { ApplicationInsights, SeverityLevel } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { getAppPathname, getAppInsightsKey } from '../static-build';

const aIPrefix = 'ADMIN_APP_INSIGHTS';
const pageRouteKey = `${aIPrefix}_ROUTE`;

const createAppInsights = () => {
  const reactPlugin = new ReactPlugin();
  const connectionString = getAppInsightsKey();

  if (!connectionString) return null;

  const appInsights = new ApplicationInsights({
    config: {
      connectionString,
      extensions: [reactPlugin],
      autoTrackPageVisitTime: true,
      disableAjaxTracking: true,
    },
  });

  appInsights.loadAppInsights();
  return appInsights;
};

/**
 * initialize telemetry with session data like carrier, realmId, etc.
 */
const initializeTelemetry = (aiInstance, sessionData) => {
  if (!aiInstance) return;

  aiInstance.addTelemetryInitializer((envelope) => {
    envelope.data = envelope.data || {};
    envelope.tags = envelope.tags || {};
    envelope.data.applicationName = 'Returns MFE';
    envelope.data.carrier = sessionData?.carrier;
    // TODO add custom data like realmId, SSO user data, etc..
    // envelope.data['realmId'] = sessionData?.realm;
  });

  if (typeof window !== 'undefined' && !window[aIPrefix]) {
    window[aIPrefix] = aiInstance;
  }
};

const getAIInstance = () => {
  if (typeof window === 'undefined') return null;
  return window[aIPrefix] || null;
};

const logException = ({ eventName, exception }) => {
  getAIInstance()?.trackException({ exception, severityLevel: SeverityLevel.Error }, {
    'admin-portal-dashboard-event': eventName,
  });
};

const logEvent = ({ eventName, message } = {}) => {
  getAIInstance()?.trackEvent({ name: eventName }, {
    'admin-portal-dashboard-event': eventName,
    message,
  });
};

const trackPageView = (router) => {
  if (typeof window !== 'undefined') {
    const pageName = getAppPathname();
    // avoid sending the same page view multiple times
    if (window[pageRouteKey] === pageName) return;

    const properties = {
      route: router.route,
    };
    if (router.query) {
      Object.keys(router.query).forEach((key) => {
        properties[`query.${key}`] = router.query[key];
      });
    }

    window[pageRouteKey] = pageName;
    getAIInstance()?.trackPageView({ name: pageName, properties });
  }
};

const flush = () => {
  getAIInstance()?.flush();
};

export default {
  createAppInsights,
  initializeTelemetry,
  logException,
  logEvent,
  trackPageView,
  flush,
  isEnabled: !!getAppInsightsKey(),
};
